/**
 * Shared vocabulary for the Instrument Themes screens.
 *
 * A theme selects one or both browse features and carries a matching filter
 * branch per selected feature. The filter *fields* are never hard-coded — they
 * come from `adminGetInstrumentThemeFilterStructure` — but the feature list and
 * the feature↔branch routing are stable contract and live here.
 */

export const FEATURES = ['FINANCIAL_INSTRUMENT', 'COLLECTION'];

export const featureLabel = feature =>
  ({
    FINANCIAL_INSTRUMENT: 'Financial Instruments',
    COLLECTION: 'Collections',
  }[feature] || feature);

export const FEATURE_OPTIONS = FEATURES.map(value => ({
  value,
  label: featureLabel(value),
}));

/**
 * What selecting a feature actually does, for the create/edit picker.
 *
 * The checkbox row previously showed only the title-cased enum, which never
 * said what the choice changed.
 */
export const FEATURE_DESCRIPTION = {
  FINANCIAL_INSTRUMENT:
    'Applies this theme when investors browse stocks, bonds, funds and IPOs.',
  COLLECTION: 'Applies this theme when investors browse curated collections.',
};

/**
 * Each feature maps to exactly one filter branch key. The structure endpoint
 * returns this as `filterField`, but we keep the fallback here so the editor can
 * group panels even before metadata resolves.
 */
export const FEATURE_BRANCH = {
  FINANCIAL_INSTRUMENT: 'financialInstrument',
  COLLECTION: 'collection',
};

export const BRANCH_FEATURE = {
  financialInstrument: 'FINANCIAL_INSTRUMENT',
  collection: 'COLLECTION',
};

/**
 * Known ID-typed filter fields that map to a real entity picker. Anything not
 * listed falls back to a paste-ObjectId tag input, so the form stays fully
 * metadata-driven — new ID fields still work, just without a labelled dropdown.
 */
export const ID_FIELD_SOURCES = {
  industries: 'INDUSTRY',
  industry: 'INDUSTRY',
  countries: 'COUNTRY',
  country: 'COUNTRY',
  exchange: 'EXCHANGE',
  exchanges: 'EXCHANGE',
  riskProfileId: 'RISK_PROFILE',
  collectionGroup: 'COLLECTION_GROUP',
  // Dependent on the branch's own assetClassKey + instrumentKey — the picker
  // stays disabled until both are chosen.
  id: 'INSTRUMENT',
  group: 'INSTRUMENT_GROUP',
};

/** Recommendation operations return unions; HTTP 200 is not success. */
export const isGqlError = result =>
  !result ||
  result.__typename === 'Error' ||
  (typeof result.statusCode === 'number' && result.statusCode >= 400);

export const errorMessage = (result, fallback = 'Something went wrong') =>
  result?.message || fallback;

/**
 * Reads a mutation result into either its payload or one message to show.
 * Handles both the `Error` union member and top-level `errors[]` (scalar/enum
 * validation), which resolve quietly under `errorPolicy: 'all'`.
 */
export const readMutation = ({data, errors}, field) => {
  if (errors?.length) return {error: errors[0].message};
  const payload = data?.[field];
  if (isGqlError(payload)) return {error: errorMessage(payload)};
  return {payload};
};

const ID_PATTERN = /^[a-f\d]{24}$/i;
export const looksLikeObjectId = value => ID_PATTERN.test(String(value || ''));

/**
 * Coerces a raw form value into the scalar/list the filter input expects.
 * Returns `undefined` for values that should be omitted entirely — but keeps an
 * explicit `false`, which is a meaningful required value (e.g. `explore`).
 */
export const coerceFieldValue = (field, raw) => {
  if (raw === undefined || raw === null) return undefined;

  if (field.isList) {
    const list = Array.isArray(raw) ? raw : [raw];
    const cleaned = list.filter(item => item !== undefined && item !== '');
    return cleaned.length
      ? cleaned.map(item => coerceScalar(field, item))
      : undefined;
  }

  if (field.type === 'Boolean') return Boolean(raw);
  if (raw === '') return undefined;
  return coerceScalar(field, raw);
};

const coerceScalar = (field, raw) => {
  if (field.type === 'Int') return Number(raw);
  return raw;
};

/**
 * Builds one branch's input object from its metadata + form values, dropping
 * empties. Explicit `false` survives because `coerceFieldValue` preserves it.
 */
export const buildBranchInput = (fields, values = {}) => {
  const input = {};
  fields.forEach(field => {
    const value = coerceFieldValue(field, values[field.name]);
    if (value !== undefined) input[field.name] = value;
  });
  return input;
};

/** A branch is missing a required field if any required metadata field is empty. */
export const missingRequiredFields = (fields, values = {}) =>
  fields
    .filter(field => field.required)
    .filter(field => {
      const value = coerceFieldValue(field, values[field.name]);
      // Booleans are complete once set; false is valid. Everything else must
      // resolve to a non-empty value.
      if (field.type === 'Boolean')
        return values[field.name] === undefined || values[field.name] === null;
      return value === undefined || (field.isList && !value.length);
    })
    .map(field => field.name);

/**
 * Fallback help for filter fields, used when the structure endpoint supplies
 * no `description` — which today is 47 of the 49 fields it returns.
 *
 * Every line here is taken from the admin integration guide. Fields the guide
 * does not define (mostTraded, highYield, popular, …) deliberately get no
 * tooltip rather than an invented one: a confident wrong explanation of a
 * filter that ships to mobile users is worse than none.
 */
const FIELD_HELP = {
  assetClassKey:
    'Required for a financial-instrument preset. Together with the instrument key it decides which instruments the theme resolves to.',
  instrumentKey:
    'Required for a financial-instrument preset. The concrete type within the asset class — a bond, a mutual fund, a public equity, an IPO.',
  // These three carry descriptions on FinancialInstrumentFilter in the gateway
  // schema, but the theme structure endpoint does not forward them — it sends
  // a description for 2 of its 49 fields. Copied here until it does.
  explore:
    'Required. Turn on to have the target query return grouped data, which is what the mobile app consumes. Off is a real submitted value here, not "unset".',
  noGroup:
    'Returns only instruments that belong to no group. Specific to managed funds.',
  popular:
    'Orders by what has actually been bought recently rather than by any curated grouping. Backs the "Popular right now" rail shown before a viewer has a risk profile.',
  group:
    'Narrows the theme to one instrument group — the groups built on the instruments list under Grouped. Only funds have any today, so this is empty for other asset classes.',
  groups: 'Returns the groups themselves rather than the instruments in them.',
  id: 'Pins the theme to one specific instrument.',
};

const BOOLEAN_HELP =
  'Off is submitted as an explicit false, not left unset. Clear it only by deselecting the feature.';

/**
 * @param feature the branch this field belongs to — kept in the signature
 *   because the two branches name the same concept differently
 *   (`industries` on financial instruments, `industry` on collections).
 */
export const fieldHelp = (feature, field) => {
  if (FIELD_HELP[field?.name]) return FIELD_HELP[field.name];
  if (field?.type === 'Boolean' && !field?.enumValues?.length)
    return BOOLEAN_HELP;
  if (field?.defaultValue !== undefined && field?.defaultValue !== null)
    return `Defaults to ${field.defaultValue} on a new theme.`;
  return undefined;
};

/** Panel-level help — the branch rules that are invisible in the fields. */
export const FILTER_PANEL_HELP =
  'Selected features and filled branches must match exactly: a selected feature with no branch is rejected, and so is a branch whose feature is not selected. On save the whole envelope is replaced, not merged — what you see here is what mobile gets.';

/**
 * Human labels for filter fields.
 *
 * The metadata names are GraphQL input keys, and showing them raw made the
 * form read like a schema dump — "assetClassKey", "mostTradedWithinDays",
 * "yeildSort" (the backend's own typo). The generic rule below splits
 * camelCase and sentence-cases it; the overrides cover the ones where that
 * still reads badly or where the key hides what the field means.
 */
const FIELD_LABELS = {
  assetClassKey: 'Asset class',
  instrumentKey: 'Instrument type',
  riskProfileId: 'Risk profile',
  collectionGroup: 'Collection group',
  id: 'Specific instrument',
  group: 'Instrument group',
  noGroup: 'Exclude grouped',
  forDabaPro: 'Daba Pro only',
  ipoStatus: 'IPO status',
  ipoPhase: 'IPO phase',
  // The backend spells this one "yeildSort"; the label should not repeat it.
  yeildSort: 'Yield sort',
  mostTradedWithinDays: 'Most traded within (days)',
  includeFacets: 'Include facets',
  endsBefore: 'Ends before',
  all: 'Include all',
  orderType: 'Order type',
};

export const humanizeField = name => {
  if (!name) return '';
  if (FIELD_LABELS[name]) return FIELD_LABELS[name];
  const words = String(name)
    // camelCase → spaced, keeping acronym runs together (IPOStatus → IPO Status)
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/([A-Z]+)([A-Z][a-z])/g, '$1 $2')
    .toLowerCase()
    .trim();
  return words.charAt(0).toUpperCase() + words.slice(1);
};

/** Enum members are SCREAMING_SNAKE in the schema; read them as words. */
export const humanizeEnum = value => {
  if (!value) return '';
  const raw = String(value);
  // Some enums are already written for humans (Active, InActive).
  if (!/^[A-Z0-9_]+$/.test(raw)) return raw;
  const words = raw.toLowerCase().split('_').join(' ');
  return words.charAt(0).toUpperCase() + words.slice(1);
};
